import { Button, DatePicker, Space } from "antd";
import { useTranslation } from "react-i18next";
import {
  DATE_FILTER_PRESET_DEFS,
  DATE_ONLY_FORMAT,
  DATE_TIME_FORMAT,
  getEnabledDateFilterPresets,
  resolveDatePreset,
} from "../lib/dateFilterPresets";
import { readCachedSystemConfig } from "../lib/systemConfig";

const { RangePicker } = DatePicker;

/**
 * Shared from/to date (and optional time) filter with configurable presets.
 * Start and end are two separate, clearly labeled pickers (not one combined
 * range field) so it's obvious which value is which at a glance.
 */
export default function DateRangeFilter({
  value,
  onChange,
  showTime = true,
  allowClear = true,
  size = "middle",
  style,
  presets,
  showPresets = true,
  disabled = false,
}) {
  const { t } = useTranslation();
  const enabledKeys = presets || getEnabledDateFilterPresets(readCachedSystemConfig());
  const defs = DATE_FILTER_PRESET_DEFS.filter((d) => enabledKeys.includes(d.key));

  const applyPreset = (key) => {
    const next = resolveDatePreset(key, { withTime: showTime });
    onChange?.(next);
  };

  const [start, end] = value || [null, null];

  const setStart = (date) => onChange?.([date || null, end || null]);
  const setEnd = (date) => onChange?.([start || null, date || null]);

  return (
    <Space wrap size={8} style={style} align="center">
      {showPresets &&
        defs.map(({ key, labelKey, defaultLabel }) => (
          <Button
            key={key}
            size="small"
            disabled={disabled}
            style={{ borderRadius: 7 }}
            onClick={() => applyPreset(key)}
          >
            {t(labelKey, defaultLabel)}
          </Button>
        ))}
      <div style={{ display: "flex", alignItems: "center", gap: 6, direction: "ltr" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--t-muted, #64748b)" }}>
            {t("dateRangeStart", "Start")}
          </span>
          <DatePicker
            value={start}
            onChange={setStart}
            showTime={showTime ? { format: "HH:mm" } : false}
            format={showTime ? DATE_TIME_FORMAT : DATE_ONLY_FORMAT}
            allowClear={allowClear}
            size={size}
            disabled={disabled}
            placeholder={t("dateRangeStart", "Start")}
            style={{ borderRadius: 9 }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--t-muted, #64748b)" }}>
            {t("dateRangeEnd", "End")}
          </span>
          <DatePicker
            value={end}
            onChange={setEnd}
            showTime={showTime ? { format: "HH:mm" } : false}
            format={showTime ? DATE_TIME_FORMAT : DATE_ONLY_FORMAT}
            allowClear={allowClear}
            size={size}
            disabled={disabled}
            placeholder={t("dateRangeEnd", "End")}
            style={{ borderRadius: 9 }}
          />
        </div>
      </div>
    </Space>
  );
}

export { RangePicker };
